import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CurrencyPipe, DatePipe} from '@angular/common';
import {ConfirmationService, MessageService} from 'primeng/api';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleService} from '../../../../../service/offer-rule.service';
import {EligibleOfferSetModel, OfferSetOfferModel} from '../../../../../model/eligible-offer-set.model';

@Component({
  selector: 'auto-offer-set-rules',
  templateUrl: './offer-set-rules.component.html',
  styleUrls: ['./offer-set-rules.component.scss'],
  standalone: false,
  providers: [CurrencyPipe, DatePipe]
})
export class OfferSetRulesComponent implements OnInit, OnChanges {
  @Input() accountId: number;
  @Input() isOfferRegistryProcessingActive = false;

  @Output() ruleConfigurationChanged = new EventEmitter<void>();

  rules: OfferRuleModel[] = [];
  publicRules: OfferRuleModel[] = [];
  privateRules: OfferRuleModel[] = [];
  filteredPublicRules: OfferRuleModel[] = [];
  filteredPrivateRules: OfferRuleModel[] = [];
  eligibleOfferSets: EligibleOfferSetModel[] = [];
  offerSetOfferTextMap: {[key: number]: string} = {};
  tableLoading = false;
  dialogVisible = false;
  savingRule = false;
  dialogTitle = '';
  dialogMode: 'create' | 'edit' = 'create';
  dialogRuleType = 'offerSetRule';
  selectedRule: OfferRuleModel | null = null;
  searchValue = '';
  hasLoaded = false;

  ruleTypeFilterOptions = [
    {label: 'All', value: 'all'},
    {label: 'Offer Set Rules', value: 'offerSetRule'},
    {label: 'Dollar Discounts', value: 'discountRule'}
  ];

  selectedRuleTypeFilter = 'all';

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private offerRuleService: OfferRuleService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe
  ) {
  }

  /**
   * Init offer set rules UI
   */
  ngOnInit(): void {
    this.handleAccountChange(this.accountId);
  }

  /**
   * Handles changes to the component's input properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    const accountChange = changes['accountId'];
    if (accountChange) {
      this.handleAccountChange(accountChange.currentValue);
    }
  }

  /**
   * Loads eligible offer sets for the current account
   */
  loadEligibleOfferSets(): void {
    const canLoad = this.accountId !== null && this.accountId !== undefined;
    if (canLoad) {
      this.offerRuleService.getEligibleOfferSets(this.accountId).subscribe({
        next: (offerSets) => {
          this.eligibleOfferSets = this.buildEligibleOfferSetOptions(offerSets);
          this.buildOfferSetOfferTextMap();
        },
        error: () => {
          this.eligibleOfferSets = [];
          this.offerSetOfferTextMap = {};
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load eligible offer sets.'
          });
        }
      });
    } else {
      this.eligibleOfferSets = [];
      this.offerSetOfferTextMap = {};
    }
  }


  /**
   * Loads all active offer set rules for the current account
   */
  loadRules(): void {
    const canLoad = this.accountId !== null && this.accountId !== undefined;
    this.tableLoading = true;
    if (canLoad) {
      this.offerRuleService.getOfferRules(this.accountId).subscribe({
        next: (rules) => {
          this.rules = rules;
          this.applyFilters();
          this.tableLoading = false;
        },
        error: () => {
          this.tableLoading = false;
          this.rules = [];
          this.publicRules = [];
          this.privateRules = [];
          this.filteredPublicRules = [];
          this.filteredPrivateRules = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load offer set rules.'
          });
        }
      });
    } else {
      this.tableLoading = false;
      this.rules = [];
      this.publicRules = [];
      this.privateRules = [];
      this.filteredPublicRules = [];
      this.filteredPrivateRules = [];
    }
  }

  /**
   * Handles search input changes for the offer set rules table
   * @param searchTerm
   * @param table
   */
  onSearch(searchTerm: string): void {
    this.searchValue = searchTerm;
    this.applyFilters();
  }

  /**
   * Handles changes to the rule type filter
   * @param filterValue
   */
  onRuleTypeFilterChange(filterValue: string): void {
    this.selectedRuleTypeFilter = filterValue;
    this.applyFilters();
  }

  /**
   * Opens Create Rule dialog
   * @param ruleType
   */
  openCreateDialog(ruleType: string): void {
    if (!this.isOfferRegistryProcessingActive) {
      this.selectedRule = null;
      this.dialogMode = 'create';
      this.dialogRuleType = ruleType;
      this.dialogTitle = 'Create Rule';
      this.dialogVisible = true;
    }
  }

  /**
   * Opens Edit Rule dialog
   * @param rule
   */
  openEditDialog(rule: OfferRuleModel): void {
    if (!this.isOfferRegistryProcessingActive) {
      this.selectedRule = rule;
      this.dialogMode = 'edit';
      this.dialogRuleType = this.getRuleTypeFromRule(rule);
      this.dialogTitle = 'Edit Rule';
      this.dialogVisible = true;
    }
  }

  /**
   * Deletes a rule
   * @param rule
   */
  deleteRule(rule: OfferRuleModel): void {
    const hasId = rule.id !== null && rule.id !== undefined;

    if (!this.isOfferRegistryProcessingActive && hasId && rule.id !== undefined && rule.id !== null) {
      this.openDeleteConfirmation(rule.id);
    }
  }

  /**
   * Opens confirmation dialog for deleting a rule
   */
  private openDeleteConfirmation(ruleId: number): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this rule?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        this.deleteConfirmedRule(ruleId);
      }
    });
  }

  /**
   * Deletes a confirmed rule
   */
  private deleteConfirmedRule(ruleId: number): void {
    if (!this.isOfferRegistryProcessingActive) {
      this.offerRuleService.deleteOfferRule(ruleId).subscribe({
        next: () => {
          this.loadRules();
          this.ruleConfigurationChanged.emit();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete rule.'
          });
        }
      });
    }
  }

  /**
   * Closes the dialog
   */
  closeDialog(): void {
    this.dialogVisible = false;
    this.savingRule = false;
  }

  handleDialogClosed(): void {
    this.dialogVisible = false;
    this.savingRule = false;
    this.dialogMode = 'create';
    this.dialogRuleType = 'offerSetRule';
    this.selectedRule = null;
  }

  handleDialogSaved(): void {
    this.dialogVisible = false;
    this.savingRule = false;
    this.dialogMode = 'create';
    this.dialogRuleType = 'offerSetRule';
    this.selectedRule = null;
    this.loadRules();
    this.ruleConfigurationChanged.emit();
  }

  /**
   * Gets label for rule in table (offer set name or Dollar Discount)
   * @param rule
   */
  getOfferSetName(rule: OfferRuleModel): string {
    let name = 'Dollar Discount';
    const hasOfferSet = rule.offerSetId !== null && rule.offerSetId !== undefined;
    if (hasOfferSet && rule.offerSetId !== undefined && rule.offerSetId !== null) {
      const matchingOfferSet = this.eligibleOfferSets.find((offerSet) => offerSet.id === rule.offerSetId);
      const hasTitle = matchingOfferSet !== undefined && matchingOfferSet !== null && matchingOfferSet?.title;
      if (hasTitle && matchingOfferSet && matchingOfferSet.title) {
        name = matchingOfferSet.title;
      } else {
        name = 'Offer Set ' + rule.offerSetId;
      }
    }
    return name;
  }

  /**
   * Builds link to editor for offer set in table
   * @param rule
   */
  getOfferSetLink(rule: OfferRuleModel): string | null {
    let link = null;
    const hasOfferSet = rule.offerSetId !== null && rule.offerSetId !== undefined;
    const hasAccount = this.accountId !== null && this.accountId !== undefined;
    if (hasOfferSet && hasAccount && rule.offerSetId !== undefined && rule.offerSetId !== null) {
      link = `/new-specials/${this.accountId}#/specials/edit/${rule.offerSetId}`;
    }
    return link;
  }

  /**
   * Builds map of offer set IDs to offer text for display in table
   * @private
   */
  private buildOfferSetOfferTextMap(): void {
    const textMap: {[key: number]: string} = {};
    const hasOfferSets = this.eligibleOfferSets !== null && this.eligibleOfferSets !== undefined;
    if (hasOfferSets) {
      for (const offerSet of this.eligibleOfferSets) {
        const hasId = offerSet.id !== undefined && offerSet.id !== null;
        if (hasId) {
          const offerText = this.composeOfferText(offerSet);
          if (offerText !== '') {
            textMap[offerSet.id] = offerText;
          }
        }
      }
    }
    this.offerSetOfferTextMap = textMap;
  }

  /**
   * Composes offer text for display in table
   * @param offerSet
   * @private
   */
  private composeOfferText(offerSet: EligibleOfferSetModel): string {
    let offerText = '';
    const parts: string[] = [];
    const offers = this.resolveOfferSetOffers(offerSet);
    const leaseTexts = this.buildLeaseTexts(offers);
    const financeText = this.buildSingleFinanceText(offers);
    const aprText = this.buildSingleAprText(offers);
    if (leaseTexts.standard !== '') {
      parts.push(leaseTexts.standard);
    }
    if (leaseTexts.alternate !== '') {
      parts.push(leaseTexts.alternate);
    }
    if (financeText !== '') {
      parts.push(financeText);
    }
    if (aprText !== '') {
      parts.push(aprText);
    }
    if (parts.length > 0) {
      offerText = parts.join(' | ');
    }
    return offerText;
  }

  /**
   * Resolves offers for an offer set
   * @param offerSet
   * @private
   */
  private resolveOfferSetOffers(offerSet: EligibleOfferSetModel): OfferSetOfferModel[] {
    let offers: OfferSetOfferModel[] = [];
    const hasOffers = offerSet.offers !== undefined && offerSet.offers !== null;
    if (hasOffers && offerSet.offers) {
      offers = offerSet.offers;
    }
    return offers;
  }

  /**
   * Builds text for standard and alt lease for offer display in table
   * @param offers
   * @private
   */
  private buildLeaseTexts(offers: OfferSetOfferModel[]): {standard: string; alternate: string} {
    let standardLease = '';
    let alternateLease = '';
    for (const offer of offers) {
      const isLease = offer.type === 'lease';
      if (isLease) {
        const leaseText = this.buildLeaseText(offer);
        const hasLeaseText = leaseText !== '';
        if (hasLeaseText) {
          const isAlternateLease = offer.leaseType !== undefined && offer.leaseType !== null && offer.leaseType !== 'lease';
          if (isAlternateLease && alternateLease === '') {
            alternateLease = leaseText;
          } else if (!isAlternateLease && standardLease === '') {
            standardLease = leaseText;
          }
        }
      }
    }
    return {standard: standardLease, alternate: alternateLease};
  }

  /**
   * Builds text for finance for offer display in table
   * @param offers
   * @private
   */
  private buildSingleFinanceText(offers: OfferSetOfferModel[]): string {
    let financeText = '';
    for (const offer of offers) {
      const isFinance = offer.type === 'finance';
      if (isFinance && financeText === '') {
        financeText = this.buildFinanceText(offer);
      }
    }
    return financeText;
  }

  /**
   * Builds text for APR for offer display in table
   * @param offers
   * @private
   */
  private buildSingleAprText(offers: OfferSetOfferModel[]): string {
    let aprText = '';
    for (const offer of offers) {
      const isApr = offer.type === 'apr';
      if (isApr && aprText === '') {
        aprText = this.buildAprText(offer);
      }
    }
    return aprText;
  }

  /**
   * Builds text for lease for offer display in table
   * @param offer
   * @private
   */
  private buildLeaseText(offer: OfferSetOfferModel): string {
    let leaseText = '';
    let label = 'Lease';
    const isAlternateLease = offer.leaseType !== undefined && offer.leaseType !== null && offer.leaseType !== 'lease';
    if (isAlternateLease) {
      label = 'Alternate Lease';
    }
    const amount = this.formatCurrencyValue(offer.value);
    if (amount !== '') {
      leaseText = label + ' for ' + amount + '/mo';
    }
    return leaseText;
  }

  /**
   * Builds text for finance for offer display in table
   * @param offer
   * @private
   */
  private buildFinanceText(offer: OfferSetOfferModel): string {
    let financeText = '';
    const amount = this.formatCurrencyValue(offer.value);
    if (amount !== '') {
      financeText = 'Finance for ' + amount + '/mo';
    }
    return financeText;
  }

  /**
   * Builds text for APR for offer display in table
   * @param offer
   * @private
   */
  private buildAprText(offer: OfferSetOfferModel): string {
    let aprText = '';
    let aprValue = this.formatAprValue(offer.apr);
    if (aprValue === '') {
      aprValue = this.formatAprValue(offer.value);
    }
    if (aprValue !== '') {
      aprText = aprValue + '% APR';
    }
    return aprText;
  }

  /**
   * Formats currency value for display
   * @param value
   * @private
   */
  private formatCurrencyValue(value: unknown): string {
    let formatted = '';
    const numericValue = this.resolveNumberValue(value);
    if (numericValue !== null) {
      const transformed = this.currencyPipe.transform(numericValue, 'USD', 'symbol', '1.0-0');
      if (transformed !== null) {
        formatted = transformed;
      }
    }
    return formatted;
  }

  /**
   * Formats APR value for display
   * @param value
   * @private
   */
  private formatAprValue(value: unknown): string {
    let formatted = '';
    const numericValue = this.resolveNumberValue(value);
    if (numericValue !== null) {
      formatted = numericValue.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2});
    }
    return formatted;
  }

  /**
   * Resolves number value from form control value
   * @param value
   * @private
   */
  private resolveNumberValue(value: unknown): number | null {
    let resolvedValue: number | null = null;
    const isNumber = typeof value === 'number';
    if (isNumber && !isNaN(value as number)) {
      resolvedValue = value as number;
    }
    const isString = typeof value === 'string';
    if (resolvedValue === null && isString) {
      const converted = Number(value);
      const isValidNumber = !isNaN(converted);
      if (isValidNumber) {
        resolvedValue = converted;
      }
    }
    return resolvedValue;
  }

  /**
   * Gets tags for vehicle criteria display in table
   * @param rule
   */
  getVehicleCriteriaTags(rule: OfferRuleModel): string[] {
    const tags: string[] = [];
    const conditionLabel = this.getConditionLabel(rule);
    tags.push('Condition: ' + conditionLabel);
    const hasYear = rule.year !== null && rule.year !== undefined;
    if (hasYear && rule.year !== undefined && rule.year !== null) {
      tags.push('Year: ' + rule.year.toString());
    }
    if (rule.make) {
      tags.push('Make: ' + rule.make);
    }
    if (rule.model) {
      tags.push('Model: ' + rule.model);
    }
    if (rule.trim) {
      tags.push('Trim: ' + rule.trim);
    }
    if (rule.vehicleLimit) {
      tags.push('Vehicle Limit: ' + rule.vehicleLimit.toString());
    }

    return tags;
  }

  /**
   * Gets normalized vehicle condition label
   * @param rule
   */
  getConditionLabel(rule: OfferRuleModel): string {
    return this.normalizeConditionValue(rule.vehicleCondition);
  }

  /**
   * Gets the latest timestamp display for a table
   * @param rule
   */
  getLatestTimestampDisplay(rule: OfferRuleModel): string {
    let display = '';
    const latestDate = this.getLatestTimestamp(rule);
    if (latestDate !== null) {
      const label = this.getLatestTimestampLabel(rule);
      const formatted = this.datePipe.transform(latestDate, 'short');
      if (formatted !== null) {
        display = label;
        if (display !== '') {
          display = display + ': ' + formatted;
        }
        if (display === '') {
          display = formatted;
        }
      }
    }
    return display;
  }

  /**
   * Handles change of account and loads in new data if necessary
   * @param accountValue
   * @private
   */
  private handleAccountChange(accountValue: unknown): void {
    const shouldLoad = accountValue !== null && accountValue !== undefined && accountValue !== '';
    if (shouldLoad && !this.hasLoaded) {
      this.hasLoaded = true;
      this.loadEligibleOfferSets();
      this.loadRules();
    }
  }

  /**
   * Gets class for status indicator for display in table
   * @param rule
   */
  getRuleStatusClass(rule: OfferRuleModel): string {
    let cssClass = 'active';
    const isActive = this.resolveStatusValue(rule.status);
    if (!isActive) {
      cssClass = 'inactive';
    }
    return cssClass;
  }

  /**
   * Resolves status value
   * @param status
   * @private
   */
  private resolveStatusValue(status: unknown): boolean {
    let isActive = true;
    const statusIsString = typeof status === 'string';
    if (statusIsString) {
      const normalizedStatus = (status as string).toLowerCase();
      if (normalizedStatus === 'inactive') {
        isActive = false;
      }
      if (normalizedStatus === 'active') {
        isActive = true;
      }
    }
    const statusIsBoolean = typeof status === 'boolean';
    if (statusIsBoolean) {
      isActive = status as boolean;
    }
    const statusIsNumber = typeof status === 'number';
    if (statusIsNumber) {
      const numericStatus = status as number;
      if (numericStatus <= 0) {
        isActive = false;
      }
      if (numericStatus > 0) {
        isActive = true;
      }
    }
    return isActive;
  }

  /**
   * Gets status label for display in table
   * @param rule
   */
  getRuleStatusLabel(rule: OfferRuleModel): string {
    let label = 'Active';
    const isActive = this.resolveStatusValue(rule.status);
    if (!isActive) {
      label = 'Inactive';
    }
    return label;
  }

  /**
   * Gets rule type
   * @param rule
   * @private
   */
  private getRuleTypeFromRule(rule: OfferRuleModel): string {
    let ruleType = 'discountRule';
    const hasOfferSet = rule.offerSetId !== null && rule.offerSetId !== undefined;
    if (hasOfferSet) {
      ruleType = 'offerSetRule';
    }
    return ruleType;
  }

  /**
   * Gets latest timestamp from rule
   * @param rule
   * @private
   */
  private getLatestTimestamp(rule: OfferRuleModel): Date | null {
    let latest: Date | null = null;
    let createdDate: Date | null = null;
    if (rule.created) {
      createdDate = new Date(rule.created);
    }
    let modifiedDate: Date | null = null;
    if (rule.modified) {
      modifiedDate = new Date(rule.modified);
    }
    if (createdDate !== null) {
      latest = createdDate;
    }
    if (modifiedDate !== null) {
      if (latest === null) {
        latest = modifiedDate;
      }
      if (latest !== null) {
        if (modifiedDate.getTime() > latest.getTime()) {
          latest = modifiedDate;
        }
      }
    }
    return latest;
  }

  /**
   * Gets label for latest timestamp display (Created/Updated)
   * @param rule
   * @private
   */
  private getLatestTimestampLabel(rule: OfferRuleModel): string {
    let label = '';
    const latestDate = this.getLatestTimestamp(rule);
    let hasModified = false;
    if (rule.modified !== null && rule.modified !== undefined && rule.modified !== '') {
      hasModified = true;
    }
    if (latestDate !== null) {
      label = 'Created';
      if (hasModified) {
        label = 'Updated';
      }
    }
    return label;
  }

  /**
   * Gets normalized value for condition display
   * @param conditionValue
   * @private
   */
  private normalizeConditionValue(conditionValue: string | undefined): string {
    let resolvedCondition = 'Any';
    if (conditionValue) {
      const uppercaseValue = conditionValue.toUpperCase();
      if (uppercaseValue === 'NEW') {
        resolvedCondition = 'New';
      } else {
        if (uppercaseValue === 'USED') {
          resolvedCondition = 'Used';
        } else {
          if (uppercaseValue === 'CPO' || uppercaseValue === 'CERTIFIED') {
            resolvedCondition = 'CPO';
          } else {
            if (uppercaseValue === 'ANY') {
              resolvedCondition = 'Any';
            }
          }
        }
      }
    }

    return resolvedCondition;
  }

  /**
   * Resolves the rule number by ensuring the input value is a valid number or null.
   *
   * @param value
   */
  private resolveRuleNumber(value: number | null | undefined): number | null {
    let resolvedValue: number | null = null;
    const hasValue = value !== null && value !== undefined;
    if (hasValue && value !== undefined && value !== null) {
      resolvedValue = value;
    }
    return resolvedValue;
  }

  /**
   * Resolves the rule string by ensuring the input value is a valid string or null.
   * @param value
   * @private
   */
  private resolveRuleString(value: string | undefined): string {
    let resolvedValue = '';
    if (value) {
      resolvedValue = value;
    }
    return resolvedValue;
  }

  /**
   * Filters rules by rule type
   * @private
   */
  private applyFilters(): void {
    this.publicRules = this.filterRulesByPrivacy(false);
    this.privateRules = this.filterRulesByPrivacy(true);

    const publicSearchResults = this.applySearchFilter(this.publicRules);
    let privateSearchResults = this.applySearchFilter(this.privateRules);
    privateSearchResults = this.applyRuleTypeFilter(privateSearchResults);

    this.filteredPublicRules = publicSearchResults;
    this.filteredPrivateRules = privateSearchResults;
  }

  /**
   * Filters rules by privacy setting
   * @param isPrivate
   * @private
   */
  private filterRulesByPrivacy(isPrivate: boolean): OfferRuleModel[] {
    return this.rules.filter((rule) => {
      const ruleIsPrivate = this.resolveIsPrivate(rule);
      return ruleIsPrivate === isPrivate;
    });
  }

  /**
   * Resolves the privacy setting for a rule
   * @param rule
   * @private
   */
  private resolveIsPrivate(rule: OfferRuleModel): boolean {
    let isPrivate = false;
    if (rule.isPrivate !== null && rule.isPrivate !== undefined) {
      isPrivate = rule.isPrivate as boolean;
    }

    return isPrivate;
  }

  /**
   * Filters rules by rule type
   * @param rules
   * @private
   */
  private applyRuleTypeFilter(rules: OfferRuleModel[]): OfferRuleModel[] {
    let filteredRules = rules;
    if (this.selectedRuleTypeFilter === 'offerSetRule') {
      filteredRules = rules.filter((rule) => this.getRuleTypeFromRule(rule) === 'offerSetRule');
    } else if (this.selectedRuleTypeFilter === 'discountRule') {
      filteredRules = rules.filter((rule) => this.getRuleTypeFromRule(rule) === 'discountRule');
    }
    return filteredRules;
  }

  /**
   * Filters rules by search value
   * @param rules
   * @private
   */
  private applySearchFilter(rules: OfferRuleModel[]): OfferRuleModel[] {
    const normalizedSearch = this.normalizeSearchValue(this.searchValue);
    let filteredRules = rules;
    if (normalizedSearch !== '') {
      filteredRules = rules.filter((rule) => this.ruleMatchesSearch(rule, normalizedSearch));
    }

    return filteredRules;
  }

  /**
   * Checks if a rule matches the search value
   * @param rule
   * @param searchValue
   * @private
   */
  private ruleMatchesSearch(rule: OfferRuleModel, searchValue: string): boolean {
    let matches = false;
    const searchableValue = this.buildSearchableValue(rule);
    if (searchableValue !== '') {
      const normalizedValue = searchableValue.toLowerCase();
      if (normalizedValue.includes(searchValue)) {
        matches = true;
      }
    }

    return matches;
  }

  /**
   * Builds the searchable value for a rule.
   * @param rule
   * @private
   */
  private buildSearchableValue(rule: OfferRuleModel): string {
    const values: string[] = [];
    const offerSetId = this.resolveRuleNumber(rule.offerSetId);
    if (offerSetId !== null) {
      values.push(String(offerSetId));
    }
    const makeValue = this.resolveRuleString(rule.make);
    if (makeValue !== '') {
      values.push(makeValue);
    }
    const modelValue = this.resolveRuleString(rule.model);
    if (modelValue !== '') {
      values.push(modelValue);
    }
    const trimValue = this.resolveRuleString(rule.trim);
    if (trimValue !== '') {
      values.push(trimValue);
    }
    const conditionValue = this.resolveRuleString(rule.vehicleCondition);
    if (conditionValue !== '') {
      values.push(conditionValue);
    }

    return values.join(' ');
  }

  /**
   * Handles change of search value
   * @param value
   * @private
   */
  private normalizeSearchValue(value: string): string {
    let normalizedValue = '';
    if (value) {
      normalizedValue = value.toLowerCase().trim();
    }

    return normalizedValue;
  }

  /**
   * Builds eligible offer set options for display in select
   * @param offerSets
   * @private
   */
  private buildEligibleOfferSetOptions(offerSets: EligibleOfferSetModel[]): EligibleOfferSetModel[] {
    return offerSets.map(offerSet => ({
      ...offerSet,
      displayTitle: offerSet.title,
      isPrivate: offerSet.isPrivate
    }));
  }
}
